import { type AreaId, isAreaId } from '../LeLens/areas/areaContract';
import { DEFAULT_AREA_PERIOD, isAreaPeriod } from '../LeLens/areas/useAreaPeriod';
import { COMMERCIAL_VIEW_PARAM_KEYS } from '../LeLens/catalogue/commercial/analyticsState';
import { COMMERCIAL_ANALYSIS_PARAM } from '../LeLens/catalogue/commercial/navigation';
import { GROWTH_PARAM_KEYS } from '../LeLens/catalogue/growth/navigation';
import { LOYALTY_PARAM_KEYS } from '../LeLens/catalogue/loyalty/navigation';
import { LENS_LIBRARY_DESTINATION_PARAM } from '../LeLens/lensLibrary';
import { EXACT_VIEW_PARAM, VIEW_REPORT_VERSION_PARAM } from '../LeLens/personalViews/viewState';

import { type ViewKey } from './viewState';

/**
 * The part of the shell's state that belongs in its address.
 *
 * The shell keeps most views on one route and gives saved Lenses their own path. Which view is open,
 * which thread is being read and which Lens is on screen were all plain component state. Nothing about it was bookmarkable or shareable, while
 * both surfaces it composes are: a chat session has `/ask-richard/shared/:sessionId` and a Lens
 * has `/le-lens/:lensId`. Sending someone "look at this" meant sending them the default view.
 *
 * A card selection carries a title as well as an id, and only the id is safe to put in a link:
 * a title written into the address would go stale the moment the card was renamed. The shell
 * therefore restores the id and looks the title up in the board the card belongs to, holding the
 * composer subject back until it resolves and dropping the selection if the card has since gone.
 */
export const SHELL_VIEW_PARAM = 'view';
export const SHELL_THREAD_PARAM = 'thread';
export const SHELL_LENS_PARAM = 'lens';
export const SHELL_CARD_PARAM = 'card';
export const SHELL_EXPORT_PARAM = 'export';
/** The governed dataset open in the Data catalogue. Only meaningful on the catalogue view. */
export const SHELL_DATASET_PARAM = 'dataset';
export const SHELL_DOMAIN_PARAM = 'domain';
export const SHELL_METRIC_PARAM = 'metric';
/** The area page open on the area view. */
export const SHELL_AREA_PARAM = 'area';
export const SHELL_CATALOGUE_SECTION_PARAM = 'section';

/**
 * Short-lived presentation switches used while the Analytics landing was being chosen.
 * They are not product state and must not survive in shared or bookmarked links now that
 * there is one canonical design.
 */
const RETIRED_PRESENTATION_PARAMS = ['palette'] as const;
const RETIRED_DOMAIN_IDS: Readonly<Record<string, string>> = {
  communications: 'growth',
  demand: 'growth',
  marketing: 'growth',
  membership: 'loyalty',
};

export type CatalogueSection = 'home' | 'data';

/**
 * Areas with a full analytics page, opened as the catalogue's domain page. The other areas have
 * no governed figures yet and open the lighter area page.
 */
const AREAS_WITH_ANALYTICS_PAGE = ['commercial', 'growth', 'loyalty'] as const;

export type AnalyticsAreaId = (typeof AREAS_WITH_ANALYTICS_PAGE)[number];

export const hasAnalyticsPage = (areaId: AreaId): areaId is AnalyticsAreaId =>
  (AREAS_WITH_ANALYTICS_PAGE as ReadonlyArray<AreaId>).includes(areaId);

/**
 * The Commercial, Growth and Loyalty pages each own a slice of the address (period, compare,
 * scope, focus, trend state), written by `analyticsState.ts`, `growth/navigation.ts` and
 * `loyalty/navigation.ts`. `decodeShellView` never reads those keys. `applyShellView` keeps them
 * while the destination is the same domain page and clears them on any other destination, so one
 * page's filters cannot ride into another page, a Lens, or a copied link.
 * If a page-owned key collides with a `SHELL_*` name, the page's key is the one to rename.
 */
const PAGE_PARAM_KEYS: ReadonlyArray<string> = [
  ...COMMERCIAL_VIEW_PARAM_KEYS,
  COMMERCIAL_ANALYSIS_PARAM,
  ...GROWTH_PARAM_KEYS,
  ...LOYALTY_PARAM_KEYS,
];

/** Open the ask-first landing when the address does not name a destination. */
export const DEFAULT_VIEW: ViewKey = 'landing';

const VIEWS: ReadonlyArray<ViewKey> = [
  'landing',
  'thread',
  'lens',
  'lensIndex',
  'area',
  'catalogue',
  'dataGuide',
  'usingAskRichard',
  'certification',
];

export type ShellUrlState = {
  view: ViewKey;
  threadId: string | null;
  lensId: string | null;
  /** The card the composer is scoped to. Only meaningful alongside the Lens that holds it. */
  cardId: string | null;
  /** The governed dataset open in the Data catalogue. Only carried on the catalogue view. */
  datasetId?: string | null;
  /** Curated analytics domain open inside the Data catalogue. */
  domainId?: string | null;
  /** Metric detail open inside a curated domain. */
  metricId?: string | null;
  /** The area page open on the area view. Only carried there. */
  areaId?: AreaId | null;
  /** Analytics defaults to Home; `data` opens the supporting governed-data library. */
  catalogueSection?: CatalogueSection;
};

/**
 * Reads the shell's state out of a query string, tolerantly. A link can be truncated by a chat
 * client or hand-edited, and neither should be an error page: an unreadable value means the
 * default. An unknown view name in particular must not leave the shell rendering nothing.
 */
export function decodeShellView(params: URLSearchParams): ShellUrlState {
  return canonicalAreaRoute(readShellView(params));
}

/**
 * True when the address names an area on the wrong view: an analytics area on the area page, or an
 * area without an analytics page on the catalogue. The shell replaces such an address once, so
 * Back never lands on a link that redirects.
 */
export function needsAreaRedirect(params: URLSearchParams): boolean {
  const raw = readShellView(params);
  return canonicalAreaRoute(raw).view !== raw.view;
}

/**
 * One address per area. Commercial, Growth and Loyalty are the catalogue's domain pages; Supply
 * and Customer Service are area pages. Older links and shared links used both views for every
 * area, so either one is mapped to the canonical view.
 */
function canonicalAreaRoute(state: ShellUrlState): ShellUrlState {
  const { areaId, domainId } = state;
  if (state.view === 'area' && areaId && hasAnalyticsPage(areaId)) {
    return {
      view: 'catalogue',
      threadId: state.threadId,
      lensId: state.lensId,
      cardId: state.cardId,
      datasetId: null,
      domainId: areaId,
      metricId: null,
      catalogueSection: 'home',
    };
  }
  if (state.view === 'catalogue' && isAreaId(domainId) && !hasAnalyticsPage(domainId)) {
    return {
      view: 'area',
      threadId: state.threadId,
      lensId: state.lensId,
      cardId: state.cardId,
      areaId: domainId,
      metricId: null,
    };
  }
  return state;
}

function readShellView(params: URLSearchParams): ShellUrlState {
  // Only the SHELL_* keys are read here. Any other param on `params` (notably the Commercial
  // page's own period/compare/scope/trend keys) is left in `params` for the page itself to read
  // via `analyticsState.ts`'s `decodeState` and is never inspected or validated by this function.
  const rawView = params.get(SHELL_VIEW_PARAM);
  const threadId = params.get(SHELL_THREAD_PARAM)?.trim() || null;
  const lensId = params.get(SHELL_LENS_PARAM)?.trim() || null;
  // Older in-app Lens links carried only `?lens=<id>`. Treat that as a Lens destination
  // rather than silently dropping a signed-in reader into the blank conversation view.
  const requestedView = VIEWS.find((candidate) => candidate === rawView) ?? (lensId ? 'lens' : DEFAULT_VIEW);
  const areaParam = params.get(SHELL_AREA_PARAM)?.trim();
  const areaId = isAreaId(areaParam) ? areaParam : null;
  // An area link with an unknown area, and the old Business Pulse list (catalogue with no domain
  // and no dataset section), both land on Lens Home. Domain, dataset and Data catalogue links
  // keep their meaning.
  const catalogueHasTarget =
    params.get(SHELL_CATALOGUE_SECTION_PARAM) === 'data' ||
    Boolean(params.get(SHELL_DATASET_PARAM)?.trim()) ||
    Boolean(params.get(SHELL_DOMAIN_PARAM)?.trim());
  const view: ViewKey =
    (requestedView === 'area' && !areaId) || (requestedView === 'catalogue' && !catalogueHasTarget)
      ? 'lensIndex'
      : requestedView;
  // A card without its board cannot be shown, so it is not carried on its own.
  const cardId = (lensId && params.get(SHELL_CARD_PARAM)?.trim()) || null;
  if (view === 'area') {
    return { view, threadId, lensId, cardId, areaId, metricId: params.get(SHELL_METRIC_PARAM)?.trim() || null };
  }
  if (view === 'catalogue') {
    const catalogueSection: CatalogueSection = params.get(SHELL_CATALOGUE_SECTION_PARAM) === 'data' ? 'data' : 'home';
    const datasetId = params.get(SHELL_DATASET_PARAM)?.trim() || null;
    const requestedDomainId = params.get(SHELL_DOMAIN_PARAM)?.trim() || null;
    const domainId =
      datasetId || catalogueSection === 'data'
        ? null
        : requestedDomainId
          ? (RETIRED_DOMAIN_IDS[requestedDomainId] ?? requestedDomainId)
          : null;
    const metricId = domainId ? params.get(SHELL_METRIC_PARAM)?.trim() || null : null;
    return { view, threadId, lensId, cardId, datasetId, domainId, metricId, catalogueSection };
  }
  return { view, threadId, lensId, cardId };
}

/**
 * The params the shell should carry, as entries to apply over whatever else is in the address.
 * A null value means remove the key, so a board back at its default keeps a clean URL rather
 * than advertising `?view=thread`.
 *
 * A lens id is only meaningful while a Lens is what is on screen, and a thread id only while a
 * thread is, so each is dropped when its own view is not the one open. Otherwise a link would
 * carry a stale id that the receiving shell would then try to restore.
 */
export function shellViewParams(state: ShellUrlState): Array<[string, string | null]> {
  const onLens = state.view === 'lens';
  const onThread = state.view === 'thread';
  return [
    [SHELL_VIEW_PARAM, state.view === DEFAULT_VIEW ? null : state.view],
    [SHELL_THREAD_PARAM, onThread ? state.threadId : null],
    [SHELL_LENS_PARAM, onLens ? state.lensId : null],
    [SHELL_CARD_PARAM, onLens && state.lensId ? state.cardId : null],
    [SHELL_AREA_PARAM, state.view === 'area' ? (state.areaId ?? null) : null],
    [SHELL_DATASET_PARAM, state.view === 'catalogue' ? (state.datasetId ?? null) : null],
    [SHELL_CATALOGUE_SECTION_PARAM, state.view === 'catalogue' && state.catalogueSection === 'data' ? 'data' : null],
    [
      SHELL_DOMAIN_PARAM,
      state.view === 'catalogue' && state.catalogueSection !== 'data' && !state.datasetId
        ? (state.domainId ?? null)
        : null,
    ],
    [
      SHELL_METRIC_PARAM,
      state.view === 'area' && state.areaId
        ? (state.metricId ?? null)
        : state.view === 'catalogue' && state.catalogueSection !== 'data' && state.domainId && !state.datasetId
          ? (state.metricId ?? null)
          : null,
    ],
  ];
}

/** The analytics domain page an address is on. The old area view of an analytics area counts as that domain. */
export function domainPageOf(params: URLSearchParams): string | null {
  const state = readShellView(params);
  if (state.view === 'area') {
    return state.areaId ?? null;
  }
  return state.view === 'catalogue' ? (state.domainId ?? null) : null;
}

function destinationDomain(state: ShellUrlState): string | null {
  if (state.view === 'area') {
    return state.areaId ?? null;
  }
  const onDomain = state.view === 'catalogue' && state.catalogueSection !== 'data' && !state.datasetId;
  return onDomain ? (state.domainId ?? null) : null;
}

/**
 * Applies {@link shellViewParams} to a copy of `current`. The askRichard home starts from an empty
 * query string. A destination that is not the domain page `current` is on drops every page-owned
 * param, and `library` is dropped unless the Lens library is open.
 */
export function applyShellView(current: URLSearchParams, state: ShellUrlState): URLSearchParams {
  const next = state.view === 'landing' ? new URLSearchParams() : new URLSearchParams(current);
  for (const key of RETIRED_PRESENTATION_PARAMS) {
    next.delete(key);
  }
  const destination = destinationDomain(state);
  if (destination === null || destination !== domainPageOf(current)) {
    for (const key of PAGE_PARAM_KEYS) {
      next.delete(key);
    }
  }
  if (state.view !== 'lensIndex') {
    next.delete(LENS_LIBRARY_DESTINATION_PARAM);
  }
  if (state.view !== 'lens' || current.get(SHELL_LENS_PARAM) !== state.lensId) {
    next.delete('lensView');
    next.delete(EXACT_VIEW_PARAM);
    next.delete(VIEW_REPORT_VERSION_PARAM);
  }
  for (const [key, value] of shellViewParams(state)) {
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The address to start a domain page from when it is opened from somewhere else: the other page's
 * params are dropped and the Business Pulse period is carried in as the page's own period.
 * It names the domain's page, so `applyShellView` treats the result as already on that domain.
 */
export function seedDomainParams(current: URLSearchParams, domainId: AnalyticsAreaId) {
  const next = new URLSearchParams(current);
  for (const key of [...PAGE_PARAM_KEYS, LENS_LIBRARY_DESTINATION_PARAM, SHELL_AREA_PARAM, SHELL_METRIC_PARAM]) {
    next.delete(key);
  }
  next.delete(SHELL_DATASET_PARAM);
  next.delete(SHELL_CATALOGUE_SECTION_PARAM);
  next.set(SHELL_VIEW_PARAM, 'catalogue');
  next.set(SHELL_DOMAIN_PARAM, domainId);
  const pulsePeriod = current.get('pulsePeriod');
  const pulseFrom = current.get('pulseFrom');
  const pulseTo = current.get('pulseTo');
  next.delete('pulsePeriod');
  next.delete('pulseFrom');
  next.delete('pulseTo');
  const periodKey = domainId === 'loyalty' ? 'loyaltyPeriod' : 'period';
  if (pulsePeriod === 'custom' && ISO_DATE.test(pulseFrom ?? '') && ISO_DATE.test(pulseTo ?? '')) {
    const [fromKey, toKey] = domainId === 'loyalty' ? ['loyaltyFrom', 'loyaltyTo'] : ['from', 'to'];
    next.set(periodKey, 'custom');
    next.set(fromKey, pulseFrom as string);
    next.set(toKey, pulseTo as string);
    return next;
  }
  const period = isAreaPeriod(pulsePeriod) ? pulsePeriod : DEFAULT_AREA_PERIOD;
  // Commercial spells its default out; Growth and Loyalty leave it off the address.
  if (domainId === 'commercial') {
    next.set('period', period);
  } else if (period !== DEFAULT_AREA_PERIOD) {
    next.set(periodKey, period);
  }
  return next;
}

const LEGACY_METRIC_PARAM: Record<AnalyticsAreaId, string> = {
  commercial: 'focus',
  growth: 'growthMetric',
  loyalty: 'loyaltyFocus',
};

/**
 * Old `?view=area&area=growth&metric=growth.x` links named the focus metric with the shell's
 * `metric` key. The analytics pages read their own focus key, so it is moved there. A metric that
 * does not belong to the area is left alone.
 */
export function mapLegacyAreaMetric(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  const area = params.get(SHELL_AREA_PARAM)?.trim();
  const metric = params.get(SHELL_METRIC_PARAM)?.trim();
  if (
    params.get(SHELL_VIEW_PARAM) !== 'area' ||
    !isAreaId(area) ||
    !hasAnalyticsPage(area) ||
    !metric?.startsWith(`${area}.`)
  ) {
    return next;
  }
  next.set(SHELL_VIEW_PARAM, 'catalogue');
  next.set(SHELL_DOMAIN_PARAM, area);
  next.set(LEGACY_METRIC_PARAM[area], metric);
  next.delete(SHELL_AREA_PARAM);
  next.delete(SHELL_METRIC_PARAM);
  return next;
}

/**
 * True when the shell is showing the `'lens'` view with no real Lens id behind it: exactly the
 * state a `?view=lens&create=1`/`?view=lens&plan=<planId>` URL door's own `view=lens` leaves
 * `decodeShellView` in (those params name no `lens` id; `startLensConversation` opens a sheet on
 * top instead of a real Lens view). Left alone whenever a genuine Lens is open (`activeLensId`
 * set): a "New Lens" click from inside a real Lens must not disturb it.
 */
export function shouldResetBrokenLensView(view: ViewKey, activeLensId: string | null): boolean {
  return view === 'lens' && !activeLensId;
}

export function unifiedAskRichardPath(legacySearch: string): string {
  const legacy = new URLSearchParams(legacySearch);
  const sessionId = legacy.get('sessionId');
  if (!sessionId) {
    return '/ask-richard-unified';
  }
  const next = new URLSearchParams([
    [SHELL_VIEW_PARAM, 'thread'],
    [SHELL_THREAD_PARAM, sessionId],
  ]);
  if (legacy.get(SHELL_EXPORT_PARAM) === 'pdf') {
    next.set(SHELL_EXPORT_PARAM, 'pdf');
  }
  return `/ask-richard-unified?${next.toString()}`;
}
